import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, Download, ShieldCheck } from 'lucide-react';
import type { Me, MfaSetup } from '@hoopify/shared';
import { api, errorMessage, fieldErrors } from '../lib/api';
import { sessionKey, useMe } from '../lib/session';
import { NewPasswordField } from '../components/PasswordField';
import { Alert, Button, Dialog, Field, LoadingBlock, PageHeader, Section } from '../components/ui';
import { NotificationSettingsSection } from '../components/notifications/Notifications';
import { formatTimeAs, setUses24Hour, useTime24 } from '../lib/time';

export function AccountPage() {
  return (
    <>
      <PageHeader title="Account" description="Your details, notifications, password and sign-in security." />
      <Profile />
      <NotificationSettingsSection />
      <TimeFormat />
      <TwoStep />
      <ChangePassword />
    </>
  );
}

function Profile() {
  const me = useMe();
  const qc = useQueryClient();
  const [name, setName] = useState(me.name);
  const save = useMutation({
    mutationFn: () => api.patch<Me>('/auth/profile', { name }),
    onSuccess: () => qc.invalidateQueries({ queryKey: sessionKey }),
  });
  const fields = fieldErrors(save.error);
  return (
    <Section title="Your details">
      <form onSubmit={(e: FormEvent) => { e.preventDefault(); save.mutate(); }} className="max-w-[420px] space-y-4" noValidate>
        <Field label="Name" autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} error={fields.name} />
        <Field label="Email" value={me.email} readOnly hint={me.emailVerified ? 'Confirmed.' : 'Not confirmed yet. Check your inbox for the link.'} />
        {save.isError && !fields.name && <Alert>{errorMessage(save.error)}</Alert>}
        <div className="flex items-center gap-3">
          <Button type="submit" loading={save.isPending} disabled={name.trim() === me.name}>
            Save
          </Button>
          {save.isSuccess && name.trim() === me.name && <span className="text-[14px] text-ok">Saved</span>}
        </div>
      </form>
    </Section>
  );
}

function TimeFormat() {
  const h24 = useTime24();
  return (
    <Section title="Times" description="How times are shown on this device. You can type them either way.">
      <div className="inline-grid grid-cols-2 gap-1 rounded-md bg-sunken p-1 text-[14px]" role="radiogroup" aria-label="Time format">
        {[false, true].map((v) => (
          <button key={String(v)} role="radio" aria-checked={h24 === v} onClick={() => setUses24Hour(v)} className={`rounded px-4 py-1.5 font-medium ${h24 === v ? 'bg-surface text-ink shadow-sm' : 'text-muted'}`}>
            {v ? `24-hour (${formatTimeAs('16:30', true)})` : `12-hour (${formatTimeAs('16:30', false)})`}
          </button>
        ))}
      </div>
    </Section>
  );
}

function ChangePassword() {
  const me = useMe();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const change = useMutation({
    mutationFn: () => api.post('/auth/password/change', { currentPassword: current, newPassword: next }),
    onSuccess: () => {
      setCurrent('');
      setNext('');
    },
  });
  const fields = fieldErrors(change.error);
  return (
    <Section title="Password" description="Changing it signs you out on your other devices.">
      <form onSubmit={(e: FormEvent) => { e.preventDefault(); change.mutate(); }} className="max-w-[420px] space-y-4" noValidate>
        <Field label="Current password" type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} error={fields.currentPassword} required />
        <NewPasswordField label="New password" name="newPassword" value={next} onChange={setNext} error={fields.newPassword} userInputs={[me.name, me.email]} />
        {change.isError && !Object.keys(fields).length && <Alert>{errorMessage(change.error)}</Alert>}
        {change.isSuccess && <Alert tone="ok">Password changed. We’ve emailed you to confirm.</Alert>}
        <Button type="submit" loading={change.isPending} disabled={!current || !next}>
          Change password
        </Button>
      </form>
    </Section>
  );
}

// ---------- Two-step sign-in ----------

function TwoStep() {
  const status = useQuery({ queryKey: ['mfa'], queryFn: () => api.get<{ enabled: boolean; recoveryCodesLeft: number }>('/auth/mfa') });
  const [codes, setCodes] = useState<string[] | null>(null);

  return (
    <Section
      title="Two-step sign-in"
      description="After your password, you’ll also enter a code from an authenticator app on your phone (such as Google Authenticator, Microsoft Authenticator or 1Password). It stops anyone who learns your password from getting in."
    >
      {status.isLoading ? (
        <LoadingBlock />
      ) : status.isError ? (
        <Alert>{errorMessage(status.error)}</Alert>
      ) : codes ? (
        <RecoveryCodes codes={codes} onDone={() => setCodes(null)} />
      ) : status.data!.enabled ? (
        <MfaOn left={status.data!.recoveryCodesLeft} onNewCodes={setCodes} />
      ) : (
        <MfaSetupFlow onEnabled={setCodes} />
      )}
    </Section>
  );
}

function MfaSetupFlow({ onEnabled }: { onEnabled: (codes: string[]) => void }) {
  const qc = useQueryClient();
  const [code, setCode] = useState('');
  const setup = useMutation({ mutationFn: () => api.post<MfaSetup>('/auth/mfa/setup') });
  const enable = useMutation({
    mutationFn: () => api.post<{ recoveryCodes: string[] }>('/auth/mfa/enable', { code }),
    onSuccess: (r) => {
      onEnabled(r.recoveryCodes);
      void qc.invalidateQueries({ queryKey: ['mfa'] });
      void qc.invalidateQueries({ queryKey: sessionKey });
    },
  });

  if (!setup.data) {
    return (
      <div className="space-y-3">
        <Alert tone="warn">Two-step sign-in is off. We recommend turning it on, especially if you’re a head coach.</Alert>
        {setup.isError && <Alert>{errorMessage(setup.error)}</Alert>}
        <Button loading={setup.isPending} onClick={() => setup.mutate()}>
          <ShieldCheck className="size-4" aria-hidden /> Turn on two-step sign-in
        </Button>
      </div>
    );
  }

  const qr = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(setup.data.qrSvg)}`;
  return (
    <ol className="max-w-[560px] space-y-6">
      <li className="grid gap-3 sm:grid-cols-[auto_1fr] sm:gap-5">
        <img src={qr} alt="QR code to scan with your authenticator app" className="size-44 rounded-md border border-line bg-white p-2" />
        <div>
          <p className="font-medium">1. Scan this code with your authenticator app</p>
          <p className="mt-1 text-[14px] text-muted">Can’t scan it? In your app, choose to enter a setup key and type this:</p>
          <code className="tnum mt-2 block rounded-md bg-sunken px-3 py-2 text-[14px] break-all select-all">{setup.data.secret.match(/.{1,4}/g)?.join(' ')}</code>
        </div>
      </li>
      <li>
        <form onSubmit={(e: FormEvent) => { e.preventDefault(); enable.mutate(); }} className="space-y-3" noValidate>
          <p className="font-medium">2. Enter the 6-digit code the app shows</p>
          <div className="flex max-w-[320px] items-start gap-2">
            <div className="flex-1">
              <Field
                label="Code"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="123456"
                className="field-input tnum tracking-[0.2em]"
                error={fieldErrors(enable.error).code}
              />
            </div>
            <Button type="submit" loading={enable.isPending} disabled={code.length !== 6} className="btn-primary mt-[26px]">
              Turn on
            </Button>
          </div>
          {enable.isError && !fieldErrors(enable.error).code && <Alert>{errorMessage(enable.error)}</Alert>}
        </form>
      </li>
    </ol>
  );
}

function RecoveryCodes({ codes, onDone }: { codes: string[]; onDone: () => void }) {
  const [copied, setCopied] = useState(false);
  const text = `Hoopify recovery codes\nEach code works once. Keep them somewhere safe.\n\n${codes.join('\n')}\n`;
  const download = () => {
    const url = URL.createObjectURL(new Blob([text], { type: 'text/plain' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'hoopify-recovery-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div className="max-w-[560px] space-y-4">
      <Alert tone="ok">Two-step sign-in is on.</Alert>
      <div>
        <p className="font-medium">Save your recovery codes</p>
        <p className="mt-1 text-[14px] text-muted">If you lose your phone, each of these codes lets you sign in once. This is the only time they’re shown.</p>
      </div>
      <ul className="tnum grid grid-cols-2 gap-x-6 gap-y-1.5 rounded-md bg-sunken px-4 py-3 font-mono text-[14px] sm:grid-cols-2">
        {codes.map((c) => (
          <li key={c}>{c}</li>
        ))}
      </ul>
      <div className="flex flex-wrap gap-2">
        <Button variant="secondary" onClick={() => navigator.clipboard.writeText(text).then(() => setCopied(true))}>
          <Copy className="size-4" aria-hidden /> {copied ? 'Copied' : 'Copy'}
        </Button>
        <Button variant="secondary" onClick={download}>
          <Download className="size-4" aria-hidden /> Download
        </Button>
        <Button onClick={onDone}>I’ve saved them</Button>
      </div>
    </div>
  );
}

function MfaOn({ left, onNewCodes }: { left: number; onNewCodes: (codes: string[]) => void }) {
  const qc = useQueryClient();
  const [dialog, setDialog] = useState<'off' | 'codes' | null>(null);
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const close = () => {
    setDialog(null);
    setPassword('');
    setCode('');
    off.reset();
    regen.reset();
  };
  const off = useMutation({
    mutationFn: () => api.post('/auth/mfa/disable', { password, code }),
    onSuccess: () => {
      close();
      void qc.invalidateQueries({ queryKey: ['mfa'] });
      void qc.invalidateQueries({ queryKey: sessionKey });
    },
  });
  const regen = useMutation({
    mutationFn: () => api.post<{ recoveryCodes: string[] }>('/auth/mfa/recovery-codes', { code }),
    onSuccess: (r) => {
      close();
      onNewCodes(r.recoveryCodes);
      void qc.invalidateQueries({ queryKey: ['mfa'] });
    },
  });
  const active = dialog === 'off' ? off : regen;
  const fields = fieldErrors(active.error);

  return (
    <div className="space-y-4">
      <Alert tone="ok">
        Two-step sign-in is on. You have {left} recovery code{left === 1 ? '' : 's'} left.
        {left <= 3 && ' Make new ones soon so you don’t get locked out.'}
      </Alert>
      <div className="flex flex-wrap gap-2">
        <Button variant="secondary" onClick={() => setDialog('codes')}>
          Make new recovery codes
        </Button>
        <Button variant="danger" onClick={() => setDialog('off')}>
          Turn off
        </Button>
      </div>
      <Dialog open={!!dialog} onClose={close} title={dialog === 'off' ? 'Turn off two-step sign-in?' : 'Make new recovery codes'}>
        <form onSubmit={(e: FormEvent) => { e.preventDefault(); active.mutate(); }} className="space-y-4" noValidate>
          <p className="text-muted">
            {dialog === 'off'
              ? 'Your account will only be protected by your password. Confirm it’s you first.'
              : 'Your old recovery codes will stop working. Enter a code from your authenticator app to continue.'}
          </p>
          {dialog === 'off' && <Field label="Password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} error={fields.password} />}
          <Field
            label={dialog === 'off' ? 'Code from your app, or a recovery code' : 'Code from your app'}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            autoComplete="one-time-code"
            className="field-input tnum"
            error={fields.code}
          />
          {active.isError && !Object.keys(fields).length && <Alert>{errorMessage(active.error)}</Alert>}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={close}>
              Cancel
            </Button>
            <Button type="submit" variant={dialog === 'off' ? 'danger' : 'primary'} loading={active.isPending}>
              {dialog === 'off' ? 'Turn off' : 'Make new codes'}
            </Button>
          </div>
        </form>
      </Dialog>
    </div>
  );
}
